import { z } from 'zod';
import { EntityIdSchema, IsoDateTimeSchema } from './common';

export const DatosInscripcionSchema = z.object({
  id: EntityIdSchema.optional(),
  participante_id: EntityIdSchema.optional(),
  nacionalidad: z.string().nullable().optional(),
  sexo: z.string().nullable().optional(),
  fecha_nacimiento: z.string().nullable().optional(),
  telefono_alternativo: z.string().nullable().optional(),
  pnf: z.string().nullable().optional(),
  sede: z.string().nullable().optional(),
  condicion_docente: z.string().nullable().optional(),
  unidad_curricular: z.string().nullable().optional(),
  nivel_instruccion: z.string().nullable().optional(),
  titulo_pregrado: z.string().nullable().optional(),
  linea_investigacion: z.string().nullable().optional(),
  titulo_postgrado: z.string().nullable().optional(),
  moodle_activo: z.string().nullable().optional(),
  usuario_moodle: z.string().nullable().optional(),
  dominio_virtual: z.string().nullable().optional(),
  discapacidad: z.string().nullable().optional(),
  requerimiento_discapacidad: z.string().nullable().optional(),
  url_ci: z.string().nullable().optional(),
  url_cv: z.string().nullable().optional(),
  url_titulo_pregrado: z.string().nullable().optional(),
  url_foto: z.string().nullable().optional(),
  compromiso: z.string().nullable().optional(),
  created_at: IsoDateTimeSchema.optional(),
  updated_at: IsoDateTimeSchema.optional(),
});
export type DatosInscripcion = z.infer<typeof DatosInscripcionSchema>;

/**
 * Forma de creación de un participante (ver docs/00-compartido/esquemas-datos.md §3).
 * Tabla `participante`: cédula, nombre, apellido, email opcional, telefono opcional, cohorte_id.
 */
export const ParticipanteSchema = z.object({
  id: EntityIdSchema,
  cedula: z.string().min(1),
  nombre: z.string(),
  apellido: z.string(),
  email: z.string().email().nullable().optional(),
  telefono: z.string().nullable().optional(),
  cohorte_id: EntityIdSchema,
  alumno_id: EntityIdSchema.nullable().optional(),
  /**
   * Si no es null, esta fila es un ALIAS de otra persona y `es_alias_de` apunta
   * al `participante.id` de la fila principal. La fila existe para que el QR ya
   * emitido (cuyo JWT lleva este `id` firmado) siga funcionando; no se borra.
   * El panel la oculta para mostrar una entrada por persona; la app la recibe
   * siempre, porque sin ella el pase impreso dejaría de validar.
   */
  es_alias_de: EntityIdSchema.nullable().optional(),
  flag_emergencia: z.union([z.literal(0), z.literal(1)]).or(z.boolean()),
  regularizado: z.union([z.literal(0), z.literal(1)]).or(z.boolean()),
  tiene_datos_inscripcion: z.union([z.literal(0), z.literal(1)]).or(z.boolean()).optional(),
  datos_inscripcion: DatosInscripcionSchema.nullable().optional(),
  created_at: IsoDateTimeSchema,
  created_via: z.enum(['forms', 'manual', 'emergencia']),
});
export type Participante = z.infer<typeof ParticipanteSchema>;

export const ParticipantePatchInputSchema = z.object({
  nombre: z.string().min(1).optional(),
  apellido: z.string().min(1).optional(),
  email: z.string().email().nullable().optional(),
  telefono: z.string().nullable().optional(),
  regularizado: z.union([z.literal(0), z.literal(1)]).or(z.boolean()).optional(),
  datos_inscripcion: DatosInscripcionSchema.partial().optional(),
});
export type ParticipantePatchInput = z.infer<typeof ParticipantePatchInputSchema>;

/**
 * Input para POST /api/v1/participantes (creación manual).
 * NOTA: el backend NO valida formato UUID de cohorte_id (lo trata como string opaco).
 *
 * `flag_emergencia` / `regularizado` / `created_via` son opcionales: el panel
 * los usa cuando registra a alguien que NO estaba en la cohorte (emergencia en
 * sala), igual que hace la app móvil. Antes solo la ruta del móvil podía
 * marcarlos, porque el schema no los aceptaba.
 */
export const ParticipanteCreateInputSchema = z.object({
  cedula: z.string().min(1).max(20),
  nombre: z.string().min(1),
  apellido: z.string().min(1),
  email: z.string().email().optional(),
  telefono: z.string().optional(),
  cohorte_id: z.string().min(1),
  /** 1 = registrado en campo (emergencia). */
  flag_emergencia: z.union([z.literal(0), z.literal(1)]).optional(),
  /** 0 = aún no regularizado (emergencia). */
  regularizado: z.union([z.literal(0), z.literal(1)]).optional(),
  created_via: z.enum(['manual', 'emergencia']).optional(),
});
export type ParticipanteCreateInput = z.infer<typeof ParticipanteCreateInputSchema>;

/**
 * Input para POST /api/v1/participantes/emergencia (creación por operador
 * en sesión, offline-first). Extiende [ParticipanteCreateInputSchema] con
 * flags que el server setea por default.
 */
export const ParticipanteEmergenciaInputSchema = ParticipanteCreateInputSchema.extend({
  flag_emergencia: z.union([z.literal(0), z.literal(1)]).optional(),
  regularizado: z.union([z.literal(0), z.literal(1)]).optional(),
  created_via: z.enum(['manual', 'emergencia']).optional(),
});
export type ParticipanteEmergenciaInput = z.infer<typeof ParticipanteEmergenciaInputSchema>;

/**
 * Input para POST /api/v1/participantes/batch (sync offline de emergencias).
 * La app móvil llama este endpoint cuando tiene emergencias que no pudo subir
 * en el momento por falta de red.
 */
export const BatchCrearParticipantesRequestSchema = z.object({
  participantes: z.array(ParticipanteEmergenciaInputSchema).min(1).max(100),
});
export type BatchCrearParticipantesRequest = z.infer<typeof BatchCrearParticipantesRequestSchema>;

/**
 * Resultado por participante en el batch. Permite a la app saber
 * cuáles se insertaron, cuáles eran duplicados y cuáles fallaron.
 */
export const BatchItemResultadoSchema = z.object({
  cedula: z.string(),
  nombre: z.string(),
  apellido: z.string(),
  server_id: z.string().nullable(),
  inserted: z.boolean(),
  duplicate: z.boolean(),
  error: z.string().optional(),
});
export type BatchItemResultado = z.infer<typeof BatchItemResultadoSchema>;

/**
 * Response de POST /api/v1/participantes/batch.
 * `inserted` = cuántos se crearon en el server.
 * `duplicates` = cuántos ya existían (no se crearon, pero la app puede marcar como sincronizados).
 * `failed` = cuántos fallaron por otro motivo (constraint, error, etc.) — la app los reintentará.
 */
export const BatchCrearParticipantesResponseSchema = z.object({
  inserted: z.number().int().nonnegative(),
  duplicates: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  results: z.array(BatchItemResultadoSchema),
});
export type BatchCrearParticipantesResponse = z.infer<typeof BatchCrearParticipantesResponseSchema>;

/**
 * Response de GET /api/v1/participantes/count.
 */
export const ParticipanteCountResponseSchema = z.object({
  total: z.number().int().nonnegative(),
});
export type ParticipanteCountResponse = z.infer<typeof ParticipanteCountResponseSchema>;